// src/components/enterprise/OmnioTemplate.tsx
import React, { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Room, RoomEvent, Track } from 'livekit-client';
import {
  Building2,
  Crown,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Globe,
  Hash,
  Image as ImageIcon,
  Link2,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  Paperclip,
  PhoneCall,
  PhoneOff,
  Plus,
  Send,
  Settings,
  Shield,
  Sparkles,
  Trash2,
  Upload,
  Volume2,
  X,
  Check,
  Copy,
  Lock,
  Users,
  Search,
  Maximize2,
  Minimize2,
  Receipt,
  Eye,
  Radio,
  Activity,
  ChevronRight
} from 'lucide-react';

const B2B_API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:4001'
    : 'https://sesgelio.com';

interface OmnioTemplateProps {
  sessionData: {
    token: string;
    theme: any;
    company: any;
    user: any;
    roles: any[];
    channels: any[];
    colleagues: any[];
  };
}

export const OmnioTemplate: React.FC<OmnioTemplateProps> = ({ sessionData }) => {
  const { token, theme } = sessionData;
  const primaryColor = theme?.primaryColor || '#6366f1';

  // Paket durumu
  const [hasActivePackage] = useState<boolean>(
    (sessionData as any).hasPackage !== false
  );

  // Temel Veriler
  const [company] = useState<any>(sessionData.company);
  const [currentUser, setCurrentUser] = useState<any>(sessionData.user);
  const [roles, setRoles] = useState<any[]>(sessionData.roles || []);
  const [channels, setChannels] = useState<any[]>(sessionData.channels || []);
  const [colleagues, setColleagues] = useState<any[]>(sessionData.colleagues || []);
  const [onlineUsers, setOnlineUsers] = useState<any[]>([]);

  // Navigasyon: 'server' (İç Sunucu) | 'dm' (Ekip İçi Özel) | 'external' (Bağımsız Dış Sunucu)
  const isGuestAccount = Boolean(sessionData.user?.isGuest);
  const [navMode, setNavMode] = useState<'server' | 'dm' | 'external'>(
    isGuestAccount ? 'external' : 'server'
  );

  // Misafir ekranını içeriden test etmek için önizleme modu
  const [previewGuestPortal, setPreviewGuestPortal] = useState<boolean>(false);

  const [activeChannel, setActiveChannel] = useState<any>(() => {
    const list = sessionData.channels || [];
    if (sessionData.user?.isGuest) {
      return list.find((c: any) => c.scope === 'external') || list[0] || null;
    }
    return (
      list.find((c: any) => c.scope === 'internal' && c.type === 'text') ||
      list[0] ||
      null
    );
  });
  const [activeDmUser, setActiveDmUser] = useState<any>(null);
  const [dmSearchQuery, setDmSearchQuery] = useState<string>('');
  const [showMemberSidebar, setShowMemberSidebar] = useState<boolean>(true);

  // Mesajlar
  const [messages, setMessages] = useState<any[]>([]);
  const [dmMessages, setDmMessages] = useState<any[]>([]);
  const [messageInput, setMessageInput] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);

  // 🎙️ GERÇEK LIVEKIT SES VE EKRAN PAYLAŞIMI STATE'LERİ
  const [activeVoiceChannel, setActiveVoiceChannel] = useState<any>(null);
  const [voiceConnecting, setVoiceConnecting] = useState<boolean>(false);
  const [voiceParticipants, setVoiceParticipants] = useState<
    { identity: string; name: string; isSpeaking: boolean }[]
  >([]);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isScreenSharing, setIsScreenSharing] = useState<boolean>(false);
  const [activeScreenTrack, setActiveScreenTrack] = useState<any>(null);
  const [isStageExpanded, setIsStageExpanded] = useState<boolean>(false);

  // Kişisel Ayarlar
  const [showUserSettings, setShowUserSettings] = useState<boolean>(false);
  const [settingsTab, setSettingsTab] = useState<'profile' | 'audio'>('profile');
  const [editUsername, setEditUsername] = useState<string>(sessionData.user?.username || '');
  const [editCustomStatus, setEditCustomStatus] = useState<string>(
    sessionData.user?.customStatus || 'Mesai başında'
  );
  const [editStatus, setEditStatus] = useState<string>(sessionData.user?.status || 'online');
  const [editAvatar, setEditAvatar] = useState<string>(sessionData.user?.avatarUrl || '');
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedInput, setSelectedInput] = useState<string>(
    sessionData.user?.selectedInputId || 'default'
  );
  const [selectedOutput, setSelectedOutput] = useState<string>(
    sessionData.user?.selectedOutputId || 'default'
  );
  const [noiseSuppression, setNoiseSuppression] = useState<boolean>(
    sessionData.user?.noiseSuppression ?? true
  );
  const [soundNotifications, setSoundNotifications] = useState<boolean>(
    sessionData.user?.soundNotifications ?? true
  );
  const [savingSettings, setSavingSettings] = useState<boolean>(false);

  // Patron Modalları
  const [showCreateChannelModal, setShowCreateChannelModal] = useState<boolean>(false);
  const [newChannelName, setNewChannelName] = useState<string>('');
  const [newChannelType, setNewChannelType] = useState<'text' | 'voice'>('text');
  const [newChannelScope, setNewChannelScope] = useState<'internal' | 'external'>('internal');
  const [showRoleModal, setShowRoleModal] = useState<boolean>(false);
  const [showAuditModal, setShowAuditModal] = useState<boolean>(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [newRoleName, setNewRoleName] = useState<string>('');
  const [newRoleColor, setNewRoleColor] = useState<string>('#10b981');

  // Dış Şirket / Müşteri Köprüsü
  const [targetCompanyCode, setTargetCompanyCode] = useState<string>('');
  const [generatedGuestUrl, setGeneratedGuestUrl] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const livekitRoomRef = useRef<Room | null>(null);
  const audioElementsRef = useRef<HTMLMediaElement[]>([]);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);

  const isPatron = Boolean(
    currentUser?.isCompanyOwner ||
      currentUser?.role?.isOwnerRole ||
      (currentUser?.role?.permissions && currentUser.role.permissions.includes('ADMIN'))
  );

  const isExternalView = isGuestAccount || previewGuestPortal;

  // 1. SOKET VE OMNIO MASAÜSTÜ KÖPRÜSÜ (postMessage Dinleyicisi)
  useEffect(() => {
    const socket = io(B2B_API_BASE, {
      path: '/b2b-socket/',
      auth: { token },
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    socket.on('b2b-online-users', (users: any[]) => setOnlineUsers(users));
    socket.on('b2b-channel-created', (ch: any) => {
      setChannels(prev => (prev.some(c => c.id === ch.id) ? prev : [...prev, ch]));
    });
    socket.on('b2b-channel-deleted', (deletedId: string) => {
      setChannels(prev => prev.filter(c => c.id !== deletedId));
    });
    socket.on('b2b-role-created', (role: any) => {
      setRoles(prev => (prev.some(r => r.id === role.id) ? prev : [...prev, role]));
    });
    socket.on('b2b-user-updated', (updatedUser: any) => {
      setColleagues(prev => prev.map(u => (u.id === updatedUser.id ? updatedUser : u)));
      if (updatedUser.id === currentUser?.id) setCurrentUser(updatedUser);
    });
    socket.on('b2b-new-message', (msg: any) => {
      setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
    });
    socket.on('b2b-new-dm', (dm: any) => {
      setDmMessages(prev => (prev.some(m => m.id === dm.id) ? prev : [...prev, dm]));
    });

    socket.on('b2b-force-logout', (data: { reason: string }) => {
      sessionStorage.removeItem('b2b_embed_session');
      alert(data.reason || 'Hesabınız silindiği için oturum kapatıldı.');
      window.location.href = '/embed';
    });

    const handleOmnioDesktopMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'OMNIO_SHARE_INVOICE' && activeChannel) {
        socket.emit('send-b2b-message', {
          channelId: activeChannel.id,
          content:
            event.data.message || `🧾 ${event.data.invoiceNo} numaralı fatura paylaşıldı.`,
          omnioPayload: {
            type: 'INVOICE_CARD',
            invoiceNo: event.data.invoiceNo || 'OMN-2026',
            customerName: event.data.customerName || 'Cari Hesap',
            amount: event.data.amount || '0,00 ₺',
            status: event.data.status || 'Onay Bekliyor'
          }
        });
      }
    };

    window.addEventListener('message', handleOmnioDesktopMessage);

    return () => {
      window.removeEventListener('message', handleOmnioDesktopMessage);
      if (livekitRoomRef.current) {
        livekitRoomRef.current.disconnect();
      }
      socket.disconnect();
    };
  }, [token, activeChannel?.id]);

  // 2. KANAL VE DM MESAJLARINI ÇEK
  useEffect(() => {
    if (!activeChannel || navMode === 'dm') return;
    socketRef.current?.emit('join-b2b-channel', activeChannel.id);

    fetch(`${B2B_API_BASE}/api/b2b/channels/${activeChannel.id}/messages`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setMessages(data))
      .catch(() => {});
  }, [activeChannel?.id, navMode, token]);

  useEffect(() => {
    if (!activeDmUser || navMode !== 'dm') return;
    fetch(`${B2B_API_BASE}/api/b2b/dm/${activeDmUser.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setDmMessages(data))
      .catch(() => {});
  }, [activeDmUser?.id, navMode, token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, dmMessages]);

  useEffect(() => {
    if (activeScreenTrack && screenVideoRef.current) {
      activeScreenTrack.attach(screenVideoRef.current);
    }
  }, [activeScreenTrack]);

  // =====================================================================
  // 🎙️ 3. GERÇEK LIVEKIT SESLİ ODA VE EKRAN PAYLAŞIMI MOTORU
  // =====================================================================
  const updateRoomParticipants = (room: Room) => {
    const list: { identity: string; name: string; isSpeaking: boolean }[] = [];
    if (room.localParticipant) {
      list.push({
        identity: room.localParticipant.identity,
        name: room.localParticipant.name || currentUser?.username || 'Ben',
        isSpeaking: room.localParticipant.isSpeaking
      });
    }
    room.remoteParticipants.forEach(p => {
      list.push({
        identity: p.identity,
        name: p.name || 'Katılımcı',
        isSpeaking: p.isSpeaking
      });
    });
    setVoiceParticipants(list);
  };

  const disconnectVoiceRoom = () => {
    if (livekitRoomRef.current) {
      livekitRoomRef.current.disconnect();
      livekitRoomRef.current = null;
    }
    audioElementsRef.current.forEach(el => el.remove());
    audioElementsRef.current = [];
    socketRef.current?.emit('b2b-voice-stop');
    setActiveVoiceChannel(null);
    setVoiceParticipants([]);
    setIsScreenSharing(false);
    setActiveScreenTrack(null);
    setIsStageExpanded(false);
  };

  const handleToggleVoiceRoom = async (channel: any) => {
    if (activeVoiceChannel?.id === channel.id) {
      disconnectVoiceRoom();
      return;
    }

    if (livekitRoomRef.current) {
      disconnectVoiceRoom();
    }

    setVoiceConnecting(true);
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/voice/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ channelId: channel.id })
      });

      const data = await res.json();
      if (!res.ok || !data.token || !data.livekitUrl) {
        throw new Error(data.error || 'Ses sunucusu bileti alınamadı.');
      }

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: noiseSuppression,
          deviceId: selectedInput !== 'default' ? selectedInput : undefined
        }
      });

      room.on(RoomEvent.ParticipantConnected, () => updateRoomParticipants(room));
      room.on(RoomEvent.ParticipantDisconnected, () => updateRoomParticipants(room));
      room.on(RoomEvent.ActiveSpeakersChanged, () => updateRoomParticipants(room));

      room.on(RoomEvent.TrackSubscribed, track => {
        if (track.kind === Track.Kind.Audio) {
          const audioEl = track.attach();
          document.body.appendChild(audioEl);
          audioElementsRef.current.push(audioEl);
        } else if (track.source === Track.Source.ScreenShare) {
          setActiveScreenTrack(track);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, track => {
        track.detach().forEach(el => el.remove());
        if (track.source === Track.Source.ScreenShare) {
          setActiveScreenTrack(null);
        }
      });

      const spec = data.planSpec ||
        (sessionData as any).planSpec || {
          audioBitrate: 256000,
          videoWidth: 2560,
          videoHeight: 1440,
          videoFps: 60
        };

      await room.connect(data.livekitUrl, data.token);
      await room.localParticipant.setMicrophoneEnabled(!isMuted, undefined, {
        audioPreset: { maxBitrate: spec.audioBitrate || 256000 }
      });

      livekitRoomRef.current = room;
      setActiveVoiceChannel(channel);
      updateRoomParticipants(room);
      socketRef.current?.emit('b2b-voice-start');
    } catch (err: any) {
      alert(err.message || 'Ses kanalına bağlanılamadı.');
    } finally {
      setVoiceConnecting(false);
    }
  };

  const handleToggleMute = async () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (livekitRoomRef.current) {
      await livekitRoomRef.current.localParticipant.setMicrophoneEnabled(!nextMuted);
    }
  };

  const handleToggleScreenShare = async () => {
    if (!livekitRoomRef.current) return;
    try {
      const spec = (sessionData as any).planSpec || {
        videoWidth: 2560,
        videoHeight: 1440,
        videoFps: 60
      };
      const nextState = !isScreenSharing;
      await livekitRoomRef.current.localParticipant.setScreenShareEnabled(nextState, {
        audio: true,
        resolution: {
          width: spec.videoWidth || 1920,
          height: spec.videoHeight || 1080,
          frameRate: spec.videoFps || 60
        }
      });
      setIsScreenSharing(nextState);
    } catch (err) {
      setIsScreenSharing(false);
    }
  };

  useEffect(() => {
    if (showUserSettings && navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices
        .enumerateDevices()
        .then(devices => {
          setAudioInputs(devices.filter(d => d.kind === 'audioinput'));
          setAudioOutputs(devices.filter(d => d.kind === 'audiooutput'));
        })
        .catch(() => {});
    }
  }, [showUserSettings]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || !socketRef.current) return;

    if (navMode === 'dm' && activeDmUser) {
      socketRef.current.emit('send-b2b-dm', {
        receiverId: activeDmUser.id,
        content: messageInput.trim()
      });
    } else if (activeChannel) {
      socketRef.current.emit('send-b2b-message', {
        channelId: activeChannel.id,
        content: messageInput.trim()
      });
    }
    setMessageInput('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeChannel) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${B2B_API_BASE}/api/b2b/channels/${activeChannel.id}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Dosya yüklenemedi.');
      }
    } catch (err) {
      alert('Dosya yükleme hatası.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setEditAvatar(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveUserSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          username: editUsername.trim(),
          customStatus: editCustomStatus.trim(),
          status: editStatus,
          avatarUrl: editAvatar,
          selectedInputId: selectedInput,
          selectedOutputId: selectedOutput,
          noiseSuppression,
          soundNotifications
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setCurrentUser(updated);
        setShowUserSettings(false);
      }
    } catch (err) {
      alert('Ayarlar kaydedilemedi.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCreateChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChannelName.trim()) return;
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newChannelName.trim(),
          type: newChannelType,
          scope: newChannelScope
        })
      });
      if (res.ok) {
        const created = await res.json();
        setChannels(prev => (prev.some(c => c.id === created.id) ? prev : [...prev, created]));
        if (created.type === 'text') setActiveChannel(created);
        setNewChannelName('');
        setShowCreateChannelModal(false);
      }
    } catch (err) {
      alert('Oda açılamadı.');
    }
  };

  const handleDeleteChannel = async (channelId: string, channelName: string) => {
    if (!window.confirm(`"#${channelName}" odasını silmek istediğinize emin misiniz?`)) return;
    await fetch(`${B2B_API_BASE}/api/b2b/channels/${channelId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    const res = await fetch(`${B2B_API_BASE}/api/b2b/roles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        name: newRoleName.trim(),
        color: newRoleColor,
        permissions: ['SEND_MESSAGES', 'CONNECT_VOICE']
      })
    });
    if (res.ok) setNewRoleName('');
  };

  const handleAssignRole = async (targetUserId: string, roleId: string) => {
    await fetch(`${B2B_API_BASE}/api/b2b/users/${targetUserId}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ roleId })
    });
  };

  const handleConnectExternalCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetCompanyCode.trim()) return;
    const res = await fetch(`${B2B_API_BASE}/api/b2b/connect-company`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ targetIdentifier: targetCompanyCode.trim() })
    });
    const data = await res.json();
    if (!res.ok) return alert(data.error || 'Şirket bulunamadı.');
    setChannels(prev => (prev.some(c => c.id === data.id) ? prev : [...prev, data]));
    setActiveChannel(data);
    setTargetCompanyCode('');
  };

  const handleCreateGuestLink = async () => {
    if (!activeChannel) return;
    const res = await fetch(`${B2B_API_BASE}/api/b2b/guest-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        channelId: activeChannel.id,
        label: `${company?.name} Canlı Görüşme`
      })
    });
    if (res.ok) {
      const data = await res.json();
      const companySlug = (company?.name || 'kurumsal')
        .toLowerCase()
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setGeneratedGuestUrl(
        `${window.location.origin}/embed/${companySlug}?guestToken=${data.inviteToken}`
      );
    }
  };

  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Kanalların Ayrıştırılması
  const internalTextChannels = channels.filter(c => c.scope === 'internal' && c.type === 'text');
  const internalVoiceChannels = channels.filter(c => c.scope === 'internal' && c.type === 'voice');
  const externalTextChannels = channels.filter(
    c => c.scope === 'external' && (c.type === 'text' || !c.type)
  );
  const externalVoiceChannels = channels.filter(c => c.scope === 'external' && c.type === 'voice');

  const otherColleagues = colleagues.filter(u => u.id !== currentUser?.id);
  const filteredColleagues = otherColleagues.filter(
    u =>
      u.username?.toLowerCase().includes(dmSearchQuery.toLowerCase()) ||
      (u.role?.name || u.roleTitle || '').toLowerCase().includes(dmSearchQuery.toLowerCase())
  );

  // ============================================================================
  // 🌐 DÜŞÜK KARMAŞALI İZOLE MÜŞTERİ / DIŞ BAĞLANTI PORTALI (GUEST PORTAL VIEW)
  // Dışarıya verilen linke tıklandığında iç sunucudan tamamen bağımsız açılan ekran
  // ============================================================================
  if (isExternalView) {
    return (
      <div className="h-screen w-full bg-[#040508] text-white font-sans flex flex-col overflow-hidden select-none relative">
        {/* Arka Plan Kurumsal Glow */}
        <div
          className="pointer-events-none fixed -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[280px] rounded-full blur-[140px] opacity-20"
          style={{ backgroundColor: primaryColor }}
        />

        {/* Üst İzole Portal Başlığı */}
        <header className="h-16 px-6 border-b border-white/[0.08] bg-[#080a10]/90 backdrop-blur-xl flex items-center justify-between shrink-0 z-10">
          <div className="flex items-center gap-3.5">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center font-black text-base text-white shadow-lg border border-white/15"
              style={{ backgroundColor: primaryColor }}
            >
              {company?.name?.charAt(0) || 'O'}
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="font-black text-sm tracking-tight text-white">
                  {company?.name || 'Kurumsal Merkez'}
                </h1>
                <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 font-bold flex items-center gap-1">
                  <Lock size={10} /> İzole Dış Görüşme & Mutabakat Odası
                </span>
              </div>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Sesgelio Kurumsal Köprü • Oda: <b className="text-gray-200">#{activeChannel?.name || 'Genel Görüşme'}</b>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            {/* Canlı Sesli Görüşmeye Katıl Butonu (Müşteri İçin Tek Tık) */}
            {activeChannel && (
              <button
                onClick={() => handleToggleVoiceRoom(activeChannel)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black transition-all shadow-lg ${
                  activeVoiceChannel?.id === activeChannel.id
                    ? 'bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'
                }`}
              >
                {activeVoiceChannel?.id === activeChannel.id ? (
                  <>
                    <PhoneOff size={14} /> Görüşmeden Ayrıl
                  </>
                ) : (
                  <>
                    <PhoneCall size={14} /> Canlı Sesli & Ekran Görüşmesine Katıl
                  </>
                )}
              </button>
            )}

            {previewGuestPortal && !isGuestAccount && (
              <button
                onClick={() => setPreviewGuestPortal(false)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-bold text-amber-300"
              >
                <Eye size={14} /> Şirket Paneline Dön
              </button>
            )}
          </div>
        </header>

        {/* İzole Portal Gövdesi */}
        <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden p-4 gap-4 max-w-7xl w-full mx-auto">
          {/* Sol / Üst: Canlı Ses ve Ekran Paylaşım Sahnesi */}
          <div className="lg:w-[420px] shrink-0 flex flex-col gap-4">
            <div className="flex-1 rounded-3xl bg-[#0b0d14] border border-white/[0.08] p-5 flex flex-col justify-between relative overflow-hidden">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] font-black uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                    <Radio size={14} className={activeVoiceChannel ? 'animate-pulse text-emerald-400' : ''} />
                    {activeVoiceChannel ? 'Canlı Bağlantı Aktif' : 'Ses & Ekran Paylaşım Sahnesi'}
                  </span>
                  <span className="text-[10px] text-gray-500 font-mono">E2EE Şifreli</span>
                </div>

                {activeScreenTrack ? (
                  <div className="relative rounded-2xl overflow-hidden bg-black border border-white/15 mb-4">
                    <video
                      ref={screenVideoRef}
                      autoPlay
                      playsInline
                      className="w-full h-52 object-contain"
                    />
                    <span className="absolute top-2.5 left-2.5 text-[9px] px-2 py-0.5 rounded bg-red-600 text-white font-black uppercase">
                      Canlı Ekran / Evrak Sunumu
                    </span>
                  </div>
                ) : (
                  <div className="h-44 rounded-2xl bg-white/[0.02] border border-dashed border-white/10 flex flex-col items-center justify-center text-center p-4 mb-4">
                    <MonitorUp size={28} className="text-gray-600 mb-2" />
                    <p className="text-xs font-bold text-gray-300">
                      {activeVoiceChannel
                        ? 'Görüşme aktif. Ekran veya fatura paylaşımı başlatabilirsiniz.'
                        : 'Sesli görüşmeye katılarak canlı mutabakat ve ekran paylaşımı yapabilirsiniz.'}
                    </p>
                  </div>
                )}

                {/* Katılımcılar */}
                <div className="space-y-2">
                  <div className="text-[10px] font-black uppercase tracking-wider text-gray-500">
                    Görüşmedeki Katılımcılar ({voiceParticipants.length})
                  </div>
                  {voiceParticipants.length === 0 ? (
                    <div className="text-xs text-gray-500 py-2">
                      Henüz ses kanalında kimse yok.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {voiceParticipants.map(vp => (
                        <div
                          key={vp.identity}
                          className={`p-2.5 rounded-2xl border flex items-center gap-2.5 transition-all ${
                            vp.isSpeaking
                              ? 'bg-emerald-500/10 border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                              : 'bg-white/[0.03] border-white/[0.06]'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center font-black text-xs text-white shrink-0">
                            {vp.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-bold truncate text-white">{vp.name}</div>
                            <div className="text-[10px] text-emerald-400">
                              {vp.isSpeaking ? 'Konuşuyor...' : 'Bağlı'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Alt Kontroller */}
              {activeVoiceChannel && (
                <div className="pt-4 border-t border-white/10 flex items-center justify-center gap-2.5">
                  <button
                    onClick={handleToggleMute}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                      isMuted
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    {isMuted ? <MicOff size={15} /> : <Mic size={15} />}
                    {isMuted ? 'Sesi Aç' : 'Sessize Al'}
                  </button>
                  <button
                    onClick={handleToggleScreenShare}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all ${
                      isScreenSharing
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    <MonitorUp size={15} />
                    Ekran Paylaş
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sağ: İzole Evrak & Mesajlaşma Akışı */}
          <div className="flex-1 rounded-3xl bg-[#0b0d14] border border-white/[0.08] flex flex-col min-h-0 overflow-hidden">
            <div className="p-4 border-b border-white/[0.07] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Hash size={16} className="text-cyan-400" />
                <span className="text-xs font-black">{activeChannel?.name || 'Dış Görüşme Odası'}</span>
              </div>
              <span className="text-[11px] text-gray-400">
                Bu odadaki yazışmalar yalnızca bu bağlantıdaki katılımcılara açıktır
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.map(msg => {
                const senderName = msg.sender?.username || msg.guestName || 'Misafir Katılımcı';
                const isMe = msg.senderUserId === currentUser?.id || msg.senderId === currentUser?.id;
                return (
                  <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                    <div className="flex items-center gap-2 mb-1 px-1">
                      <span className="text-[11px] font-bold text-gray-300">{senderName}</span>
                      <span className="text-[10px] text-gray-500">
                        {new Date(msg.createdAt).toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-3 text-xs ${
                        isMe
                          ? 'text-white rounded-br-sm shadow-lg'
                          : 'bg-white/[0.05] border border-white/10 text-gray-100 rounded-bl-sm'
                      }`}
                      style={{ backgroundColor: isMe ? primaryColor : undefined }}
                    >
                      <p className="leading-relaxed break-words">{msg.content}</p>
                      {msg.omnioPayload?.type === 'INVOICE_CARD' && (
                        <div className="mt-3 p-3 rounded-xl bg-black/50 border border-white/15 space-y-1.5">
                          <div className="text-[10px] font-black text-amber-300 uppercase flex items-center gap-1">
                            <Receipt size={12} /> OMNIO Fatura: {msg.omnioPayload.invoiceNo}
                          </div>
                          <div className="text-xs font-bold">{msg.omnioPayload.customerName}</div>
                          <div className="text-sm font-black text-emerald-400">
                            {msg.omnioPayload.amount}
                          </div>
                        </div>
                      )}
                      {msg.fileUrl && (
                        <a
                          href={`${B2B_API_BASE}${msg.fileUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2.5 flex items-center gap-2.5 p-2.5 rounded-xl bg-black/40 hover:bg-black/60 border border-white/10"
                        >
                          <FileText size={16} className="text-cyan-400 shrink-0" />
                          <span className="text-xs font-bold truncate flex-1">{msg.fileName}</span>
                          <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={handleSendMessage}
              className="p-3.5 border-t border-white/[0.08] bg-[#080a10] flex items-center gap-2.5"
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg,.webp,.zip,.rar,.xml"
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white"
                title="Evrak / Dekont Yükle"
              >
                <Paperclip size={16} />
              </button>
              <input
                type="text"
                placeholder="Mesajınızı veya mutabakat notunuzu yazın..."
                value={messageInput}
                onChange={e => setMessageInput(e.target.value)}
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-gray-500 outline-none focus:border-cyan-500/50"
              />
              <button
                type="submit"
                disabled={!messageInput.trim()}
                className="px-4 py-2.5 rounded-xl text-white font-bold text-xs flex items-center gap-1.5 disabled:opacity-40"
                style={{ backgroundColor: primaryColor }}
              >
                <Send size={14} /> Gönder
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // 🏢 ANA ŞİRKET EKOSİSTEMİ (SOL WORKSPACE RAYI + KANAL/DM AĞACI + SAHNE + KADRO)
  // ============================================================================
  return (
    <div className="h-screen w-full bg-[#05060a] text-white font-sans flex overflow-hidden select-none relative">
      {/* 1. EN SOL DİKEY EVREN RAYI (WORKSPACE DOCK) */}
      <nav className="w-[72px] bg-[#030407] border-r border-white/[0.07] flex flex-col items-center justify-between py-4 shrink-0 z-20">
        <div className="flex flex-col items-center gap-3 w-full">
          {/* Şirket Ana Sunucusu (Internal Server) */}
          <div className="relative group flex items-center justify-center w-full">
            <span
              className={`absolute left-0 w-1 rounded-r-full transition-all duration-300 ${
                navMode === 'server' ? 'h-8 bg-white' : 'h-0 group-hover:h-4 bg-white/50'
              }`}
            />
            <button
              onClick={() => {
                setNavMode('server');
                if (!activeChannel || activeChannel.scope !== 'internal') {
                  setActiveChannel(internalTextChannels[0] || channels[0] || null);
                }
              }}
              className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-base transition-all duration-200 shadow-lg ${
                navMode === 'server'
                  ? 'rounded-xl text-white ring-2 ring-white/20'
                  : 'bg-[#0e1018] text-gray-400 hover:text-white hover:rounded-xl border border-white/[0.08]'
              }`}
              style={{
                backgroundColor: navMode === 'server' ? primaryColor : undefined
              }}
              title={`${company?.name} — Şirket İçi Sunucu`}
            >
              <Building2 size={20} />
            </button>
          </div>

          {/* Ekip İçi Özel Mesajlar (DM) */}
          <div className="relative group flex items-center justify-center w-full">
            <span
              className={`absolute left-0 w-1 rounded-r-full transition-all duration-300 ${
                navMode === 'dm' ? 'h-8 bg-white' : 'h-0 group-hover:h-4 bg-white/50'
              }`}
            />
            <button
              onClick={() => {
                setNavMode('dm');
                if (!activeDmUser && otherColleagues.length > 0) {
                  setActiveDmUser(otherColleagues[0]);
                }
              }}
              className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 relative ${
                navMode === 'dm'
                  ? 'rounded-xl text-white shadow-lg ring-2 ring-white/20'
                  : 'bg-[#0e1018] text-gray-400 hover:text-white hover:rounded-xl border border-white/[0.08]'
              }`}
              style={{
                backgroundColor: navMode === 'dm' ? primaryColor : undefined
              }}
              title="Ekip İçi Özel Mesajlar (DM)"
            >
              <MessageSquare size={20} />
              {onlineUsers.length > 1 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 text-[9px] font-black text-slate-950 flex items-center justify-center">
                  {onlineUsers.length}
                </span>
              )}
            </button>
          </div>

          {/* Ayırıcı Çizgi: Sunucudan Bağımsız Dış Evren */}
          <div className="w-8 h-[1px] bg-white/10 my-1" />

          {/* Bağımsız Dış Sunucu & Müşteri Köprüsü (External Hub) */}
          <div className="relative group flex items-center justify-center w-full">
            <span
              className={`absolute left-0 w-1 rounded-r-full transition-all duration-300 ${
                navMode === 'external' ? 'h-8 bg-cyan-400' : 'h-0 group-hover:h-4 bg-cyan-400/50'
              }`}
            />
            <button
              onClick={() => {
                setNavMode('external');
                if (externalTextChannels.length > 0) {
                  setActiveChannel(externalTextChannels[0]);
                }
              }}
              className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 ${
                navMode === 'external'
                  ? 'rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 text-white shadow-[0_0_20px_rgba(6,182,212,0.35)] ring-2 ring-cyan-400/40'
                  : 'bg-[#0e1018] text-cyan-400/80 hover:text-cyan-300 hover:rounded-xl border border-cyan-500/20'
              }`}
              title="Dış Sunucu: Müşteri Linkleri & B2B Şirket Köprüsü (Bağımsız Alan)"
            >
              <Globe size={20} />
            </button>
          </div>
        </div>

        {/* Sol Alt Patron & Ayar Kısayolları */}
        <div className="flex flex-col items-center gap-2.5">
          {isPatron && (
            <>
              <button
                onClick={async () => {
                  setShowAuditModal(true);
                  try {
                    const res = await fetch(`${B2B_API_BASE}/api/b2b/company/audit-logs`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.ok) setAuditLogs(await res.json());
                  } catch (e) {}
                }}
                className="w-10 h-10 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 text-emerald-400 flex items-center justify-center transition-all"
                title="Şirket Denetim & Hareket Kayıtları (Log)"
              >
                <Activity size={17} />
              </button>

              <button
                onClick={() => setShowRoleModal(true)}
                className="w-10 h-10 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/25 text-amber-400 flex items-center justify-center transition-all"
                title="Roller & Departman Yetkileri"
              >
                <Shield size={17} />
              </button>
            </>
          )}

          <button
            onClick={() => setShowUserSettings(true)}
            className="w-10 h-10 rounded-xl bg-white/[0.04] hover:bg-white/10 border border-white/[0.08] text-gray-400 hover:text-white flex items-center justify-center transition-all"
            title="Kişisel Profil & Ses Ayarları"
          >
            <Settings size={17} />
          </button>
        </div>
      </nav>

      {/* 2. İKİNCİL KANAL / DM / DIŞ SUNUCU MENÜSÜ (SIDEBAR) */}
      <aside className="w-64 bg-[#090b11] border-r border-white/[0.07] flex flex-col justify-between shrink-0">
        {/* Sol Üst Bağlam Başlığı */}
        <div className="h-15 px-4 py-3.5 border-b border-white/[0.07] flex items-center justify-between bg-[#0b0d14]/60">
          <div className="min-w-0">
            {navMode === 'server' && (
              <>
                <div className="flex items-center gap-1.5">
                  <h2 className="font-black text-xs tracking-wide text-white truncate">
                    {company?.name}
                  </h2>
                  {isPatron && <Crown size={12} className="text-amber-400 shrink-0" />}
                </div>
                <span className="text-[10px] text-gray-400 block truncate">
                  Şirket İçi Özel Sunucu
                </span>
              </>
            )}
            {navMode === 'dm' && (
              <>
                <h2 className="font-black text-xs tracking-wide text-white">
                  Ekip İçi Mesajlaşma
                </h2>
                <span className="text-[10px] text-gray-400 block">
                  Çalışanlar Arası Birebir Görüşme
                </span>
              </>
            )}
            {navMode === 'external' && (
              <>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <h2 className="font-black text-xs tracking-wide text-cyan-300">
                    Dış Sunucu & Köprü
                  </h2>
                </div>
                <span className="text-[10px] text-gray-400 block">
                  İzole Müşteri & B2B Mutabakat Alanı
                </span>
              </>
            )}
          </div>

          {company?.b2bConnectCode && (
            <button
              onClick={() => copyText(company.b2bConnectCode, 'b2bcode')}
              className="px-2 py-1 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-[10px] font-mono text-gray-300 flex items-center gap-1 shrink-0"
              title="Şirket B2B Bağlantı Kodunu Kopyala"
            >
              <span>{company.b2bConnectCode}</span>
              {copiedKey === 'b2bcode' ? (
                <Check size={11} className="text-emerald-400" />
              ) : (
                <Copy size={11} className="text-gray-500" />
              )}
            </button>
          )}
        </div>

        {/* Kanal veya Kişi Listesi Gövdesi */}
        <div className="flex-1 overflow-y-auto p-3 space-y-5">
          {/* MOD 1: ŞİRKET İÇİ SUNUCU KANALLARI */}
          {navMode === 'server' && (
            <>
              {/* Metin Kanalları */}
              <div>
                <div className="flex items-center justify-between px-2 mb-1.5">
                  <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                    Metin & Evrak Kanalları
                  </span>
                  {isPatron && (
                    <button
                      onClick={() => {
                        setNewChannelScope('internal');
                        setNewChannelType('text');
                        setShowCreateChannelModal(true);
                      }}
                      className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
                      title="Yeni Metin Kanalı Aç"
                    >
                      <Plus size={13} />
                    </button>
                  )}
                </div>

                <div className="space-y-0.5">
                  {internalTextChannels.map(ch => {
                    const isActive = activeChannel?.id === ch.id;
                    return (
                      <div
                        key={ch.id}
                        onClick={() => setActiveChannel(ch)}
                        className={`group flex items-center justify-between px-2.5 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                          isActive
                            ? 'bg-white/[0.09] text-white shadow-sm border border-white/[0.08]'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.04]'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Hash
                            size={14}
                            style={{ color: isActive ? primaryColor : '#6b7280' }}
                            className="shrink-0"
                          />
                          <span className="truncate">{ch.name}</span>
                        </div>
                        {isPatron && internalTextChannels.length > 1 && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteChannel(ch.id, ch.name);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 p-0.5 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sesli Toplantı Odaları */}
              <div>
                <div className="flex items-center justify-between px-2 mb-1.5">
                  <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                    Sesli Toplantı Odaları
                  </span>
                  {isPatron && (
                    <button
                      onClick={() => {
                        setNewChannelScope('internal');
                        setNewChannelType('voice');
                        setShowCreateChannelModal(true);
                      }}
                      className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
                      title="Yeni Sesli Oda Aç"
                    >
                      <Plus size={13} />
                    </button>
                  )}
                </div>

                <div className="space-y-1">
                  {internalVoiceChannels.map(ch => {
                    const isConnected = activeVoiceChannel?.id === ch.id;
                    return (
                      <div
                        key={ch.id}
                        className={`rounded-xl transition-all ${
                          isConnected
                            ? 'bg-emerald-500/[0.08] border border-emerald-500/25 p-1.5'
                            : ''
                        }`}
                      >
                        <div
                          onClick={() => handleToggleVoiceRoom(ch)}
                          className={`group flex items-center justify-between px-2.5 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                            isConnected
                              ? 'text-emerald-300'
                              : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.04]'
                          }`}
                        >
                          <div className="flex items-center gap-2 truncate">
                            <Volume2
                              size={14}
                              className={
                                isConnected ? 'text-emerald-400 animate-pulse shrink-0' : 'text-gray-500 shrink-0'
                              }
                            />
                            <span className="truncate">{ch.name}</span>
                          </div>
                          {voiceConnecting && activeVoiceChannel?.id === ch.id ? (
                            <span className="text-[9px] text-amber-300">Bağlanıyor...</span>
                          ) : isConnected ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 font-black">
                              CANLI
                            </span>
                          ) : null}
                        </div>

                        {/* Ses Odasındaki Canlı Katılımcılar */}
                        {isConnected && voiceParticipants.length > 0 && (
                          <div className="pl-6 pr-2 pt-1 pb-1.5 space-y-1.5">
                            {voiceParticipants.map(vp => (
                              <div
                                key={vp.identity}
                                className="flex items-center justify-between text-[11px] text-gray-300"
                              >
                                <span className="truncate flex items-center gap-2">
                                  <span
                                    className={`w-2 h-2 rounded-full transition-all ${
                                      vp.isSpeaking
                                        ? 'bg-emerald-400 shadow-[0_0_8px_#34d399] scale-125'
                                        : 'bg-gray-600'
                                    }`}
                                  />
                                  <span className={vp.isSpeaking ? 'text-white font-bold' : ''}>
                                    {vp.name}
                                  </span>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* MOD 2: EKİP İÇİ ÖZEL MESAJLAR (DM) */}
          {navMode === 'dm' && (
            <div className="space-y-3">
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type="text"
                  placeholder="Personel veya departman ara..."
                  value={dmSearchQuery}
                  onChange={e => setDmSearchQuery(e.target.value)}
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder-gray-500 outline-none focus:border-white/20"
                />
              </div>

              <div className="px-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-gray-500">
                  Şirket Çalışanları ({filteredColleagues.length})
                </span>
              </div>

              <div className="space-y-1">
                {filteredColleagues.map(col => {
                  const isOnline = onlineUsers.some(u => u.userId === col.id);
                  const isSelected = activeDmUser?.id === col.id;
                  return (
                    <div
                      key={col.id}
                      onClick={() => setActiveDmUser(col)}
                      className={`flex items-center gap-2.5 p-2 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-white/[0.09] text-white border border-white/[0.08]'
                          : 'hover:bg-white/[0.04] text-gray-300'
                      }`}
                    >
                      <div className="relative shrink-0">
                        <img
                          src={col.avatarUrl}
                          alt={col.username}
                          className="w-8 h-8 rounded-xl object-cover border border-white/10"
                        />
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#090b11] ${
                            isOnline ? 'bg-emerald-500' : 'bg-gray-600'
                          }`}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold truncate">{col.username}</div>
                        <div className="text-[10px] text-gray-500 truncate">
                          {col.customStatus || col.role?.name || col.roleTitle}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* MOD 3: BAĞIMSIZ DIŞ SUNUCU (MÜŞTERİ LİNKLERİ & B2B ORTAK ODALAR) */}
          {navMode === 'external' && (
            <div className="space-y-4">
              {/* Müşteri Davet Linki & Önizleme Kartı */}
              <div className="p-3 rounded-2xl bg-gradient-to-b from-cyan-500/10 to-indigo-500/5 border border-cyan-500/25 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                    <Link2 size={12} /> Dış Müşteri Bağlantısı
                  </span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-200 font-bold">
                    İzole
                  </span>
                </div>
                <p className="text-[11px] text-gray-300 leading-relaxed">
                  Müşterinize veya mali müşavirinize şirket içi sunucudan bağımsız, tek tıkla katılabileceği canlı oda linki verin.
                </p>
                <div className="grid grid-cols-1 gap-1.5 pt-1">
                  <button
                    type="button"
                    onClick={handleCreateGuestLink}
                    className="w-full py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs flex items-center justify-center gap-1.5 transition-all shadow-md"
                  >
                    <Sparkles size={13} /> Davet Linki Oluştur
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewGuestPortal(true)}
                    className="w-full py-1.5 rounded-xl bg-white/[0.06] hover:bg-white/10 border border-white/10 text-gray-200 font-bold text-[11px] flex items-center justify-center gap-1.5 transition-all"
                  >
                    <Eye size={12} /> Müşteri Ekranını Önizle
                  </button>
                </div>
              </div>

              {/* Başka OMNIO Şirketine Bağlan */}
              <form
                onSubmit={handleConnectExternalCompany}
                className="p-3 rounded-2xl bg-white/[0.02] border border-white/[0.08] space-y-2"
              >
                <label className="block text-[10px] font-black uppercase tracking-wider text-indigo-300">
                  🤝 Başka Şirketle Köprü Kur
                </label>
                <input
                  type="text"
                  placeholder="Vergi No veya OMN-Kodu"
                  value={targetCompanyCode}
                  onChange={e => setTargetCompanyCode(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none focus:border-indigo-500/50"
                />
                <button
                  type="submit"
                  className="w-full py-2 rounded-xl text-xs font-bold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  Ortak Mutabakat Odası Aç
                </button>
              </form>

              {/* Dış Sunucu Odaları */}
              <div>
                <div className="flex items-center justify-between px-2 mb-1.5">
                  <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400/80">
                    Dış Görüşme Odaları ({externalTextChannels.length})
                  </span>
                  {isPatron && (
                    <button
                      onClick={() => {
                        setNewChannelScope('external');
                        setNewChannelType('text');
                        setShowCreateChannelModal(true);
                      }}
                      className="text-cyan-400 hover:text-white p-1 rounded-lg hover:bg-white/10"
                      title="Yeni Dış Müşteri Odası Aç"
                    >
                      <Plus size={13} />
                    </button>
                  )}
                </div>

                <div className="space-y-1">
                  {externalTextChannels.length === 0 ? (
                    <div className="px-2 py-4 text-center text-[11px] text-gray-500">
                      Henüz dış müşteri odası yok. "+" butonundan izole bir dış oda açabilirsiniz.
                    </div>
                  ) : (
                    externalTextChannels.map(ch => (
                      <div
                        key={ch.id}
                        onClick={() => setActiveChannel(ch)}
                        className={`flex items-center justify-between px-2.5 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                          activeChannel?.id === ch.id
                            ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30'
                            : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Globe size={14} className="text-cyan-400 shrink-0" />
                          <span className="truncate">{ch.name}</span>
                        </div>
                        <ChevronRight size={13} className=" opacity-50" />
                      </div>
                    ))
                  )}
                </div>
              </div>

              {externalVoiceChannels.length > 0 && (
                <div>
                  <div className="px-2 mb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400/80">
                      Dış Ses Odaları
                    </span>
                  </div>
                  <div className="space-y-1">
                    {externalVoiceChannels.map(ch => (
                      <div
                        key={ch.id}
                        onClick={() => handleToggleVoiceRoom(ch)}
                        className="flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-bold cursor-pointer text-gray-300 hover:bg-white/5"
                      >
                        <Volume2 size={14} className="text-emerald-400" />
                        <span className="truncate">{ch.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Aktif Ses Bağlantısı Mini Durum Barı */}
        {activeVoiceChannel && (
          <div className="p-3 bg-emerald-500/[0.08] border-t border-emerald-500/25 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <span className="text-[10px] font-black uppercase text-emerald-400 flex items-center gap-1.5">
                <PhoneCall size={11} className="animate-pulse" /> Ses Bağlantısı Aktif
              </span>
              <span className="text-xs font-bold text-white truncate block">
                {activeVoiceChannel.name}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={handleToggleScreenShare}
                className={`p-2 rounded-xl transition-all ${
                  isScreenSharing
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
                title="Ekran / Fatura Paylaş"
              >
                <MonitorUp size={14} />
              </button>
              <button
                onClick={disconnectVoiceRoom}
                className="p-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-all"
                title="Sesten Ayrıl"
              >
                <PhoneOff size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Sol Alt Kullanıcı Kartı */}
        <div className="p-3 border-t border-white/[0.07] bg-[#06070c] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative shrink-0">
              <img
                src={currentUser?.avatarUrl}
                alt={currentUser?.username}
                className="w-9 h-9 rounded-xl object-cover border border-white/15"
              />
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#06070c] ${
                  currentUser?.status === 'busy' ? 'bg-red-500' : 'bg-emerald-500'
                }`}
              />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-black text-white truncate">{currentUser?.username}</div>
              <div className="text-[10px] text-gray-400 truncate">
                {currentUser?.customStatus || currentUser?.role?.name || currentUser?.roleTitle}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={handleToggleMute}
              className={`p-2 rounded-xl transition-all ${
                isMuted
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'hover:bg-white/10 text-gray-300'
              }`}
              title={isMuted ? 'Mikrofonu Aç' : 'Mikrofonu Kapat'}
            >
              {isMuted ? <MicOff size={15} /> : <Mic size={15} />}
            </button>
          </div>
        </div>
      </aside>

      {/* 3. ORTA ANA SAHNE: ÜST BAR + CANLI SES/EKRAN SAHNESİ + MESAJ AKIŞI */}
      <main className="flex-1 flex flex-col min-w-0 bg-[#05060a] relative">
        {/* Üst Kanal / Bağlam Başlığı */}
        <header className="h-15 px-5 border-b border-white/[0.07] bg-[#080a10]/80 backdrop-blur-md flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {navMode === 'dm' ? (
              <>
                <div className="w-8 h-8 rounded-xl bg-white/[0.06] flex items-center justify-center">
                  <MessageSquare size={16} style={{ color: primaryColor }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-black text-sm text-white truncate">
                      {activeDmUser ? activeDmUser.username : 'Personel Seçin'}
                    </span>
                    {activeDmUser?.roleTitle && (
                      <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/[0.06] text-gray-300 font-bold">
                        {activeDmUser.role?.name || activeDmUser.roleTitle}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 block">
                    Şirket İçi Özel Görüşme • Vergi No: {company?.taxNumber}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                    navMode === 'external' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-white/[0.06]'
                  }`}
                >
                  {navMode === 'external' ? (
                    <Globe size={16} />
                  ) : (
                    <Hash size={16} style={{ color: primaryColor }} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-black text-sm text-white truncate">
                      {activeChannel?.name || 'Kanal Seçin'}
                    </span>
                    {navMode === 'external' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 font-bold">
                        Bağımsız Dış Oda
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400 block">
                    {navMode === 'external'
                      ? 'Dış bağlantıya açık izole mutabakat ve müşteri odası'
                      : `${company?.name} • Kurumsal İletişim ve Evrak Akışı`}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Hızlı Sesli Toplantı Başlat / Katıl */}
            {navMode !== 'dm' && activeChannel && (
              <button
                onClick={() => handleToggleVoiceRoom(activeChannel)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                  activeVoiceChannel?.id === activeChannel.id
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    : 'bg-white/[0.05] hover:bg-white/10 text-gray-200 border-white/10'
                }`}
              >
                <PhoneCall size={13} className="text-emerald-400" />
                {activeVoiceChannel?.id === activeChannel.id
                  ? 'Sesli Odadasınız'
                  : 'Bu Odada Ses Başlat'}
              </button>
            )}

            {/* Dış Müşteri Davet Linki Üret */}
            {navMode !== 'dm' && activeChannel && (
              <button
                onClick={handleCreateGuestLink}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs font-bold transition-all"
              >
                <Link2 size={13} /> Dış Müşteri Linki Ver
              </button>
            )}

            {navMode === 'server' && (
              <button
                onClick={() => setShowMemberSidebar(prev => !prev)}
                className={`p-2 rounded-xl border transition-all ${
                  showMemberSidebar
                    ? 'bg-white/10 border-white/20 text-white'
                    : 'bg-white/[0.03] border-white/10 text-gray-400 hover:text-white'
                }`}
                title="Şirket Kadrosunu Göster / Gizle"
              >
                <Users size={15} />
              </button>
            )}
          </div>
        </header>

        {/* Üretilen Müşteri Linki Bildirim Barı */}
        {generatedGuestUrl && (
          <div className="px-5 py-2.5 bg-gradient-to-r from-cyan-500/20 via-indigo-500/15 to-transparent border-b border-cyan-500/30 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-cyan-500 text-slate-950 shrink-0">
                Dış Link Hazır
              </span>
              <span className="truncate text-cyan-200 font-mono text-xs">{generatedGuestUrl}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => copyText(generatedGuestUrl, 'guestlink')}
                className="px-3 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs transition-all"
              >
                {copiedKey === 'guestlink' ? '✓ Kopyalandı' : 'Linki Kopyala'}
              </button>
              <button
                onClick={() => setPreviewGuestPortal(true)}
                className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/15 text-white font-bold text-xs"
              >
                Sayfayı Gör
              </button>
              <button
                onClick={() => setGeneratedGuestUrl(null)}
                className="text-gray-400 hover:text-white p-1"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* 🖥️ CANLI SES VE EKRAN PAYLAŞIMI SAHNESİ (STAGE VIEW) */}
        {(activeVoiceChannel || activeScreenTrack) && (
          <div
            className={`bg-[#090b12] border-b border-white/10 p-4 transition-all flex flex-col justify-between ${
              isStageExpanded ? 'h-[62vh]' : 'max-h-72'
            }`}
          >
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                <span className="text-xs font-black uppercase tracking-wider text-emerald-400">
                  Canlı Toplantı Sahnesi — {activeVoiceChannel?.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsStageExpanded(prev => !prev)}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300"
                  title={isStageExpanded ? 'Sahneyi Küçült' : 'Sahneyi Büyüt'}
                >
                  {isStageExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col md:flex-row items-center gap-4 min-h-0 overflow-hidden">
              {activeScreenTrack && (
                <div className="flex-1 h-full w-full bg-black rounded-2xl border border-white/15 relative flex items-center justify-center overflow-hidden">
                  <video
                    ref={screenVideoRef}
                    autoPlay
                    playsInline
                    className="max-h-full max-w-full object-contain"
                  />
                  <span className="absolute top-3 left-3 text-[10px] px-2.5 py-0.5 rounded-md bg-red-600 text-white font-black uppercase shadow">
                    Canlı Ekran Paylaşımı
                  </span>
                </div>
              )}

              {/* Katılımcı Kartları */}
              <div
                className={`grid gap-2.5 overflow-y-auto ${
                  activeScreenTrack
                    ? 'w-full md:w-56 grid-cols-2 md:grid-cols-1'
                    : 'w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
                }`}
              >
                {voiceParticipants.map(vp => (
                  <div
                    key={vp.identity}
                    className={`p-3 rounded-2xl border flex items-center gap-3 transition-all ${
                      vp.isSpeaking
                        ? 'bg-emerald-500/10 border-emerald-400/50 shadow-[0_0_20px_rgba(16,185,129,0.15)]'
                        : 'bg-white/[0.03] border-white/[0.08]'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-xs text-white shrink-0 ${
                        vp.isSpeaking ? 'bg-emerald-500 text-slate-950' : 'bg-white/10'
                      }`}
                    >
                      {vp.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-white truncate">{vp.name}</div>
                      <div className="text-[10px] text-gray-400">
                        {vp.isSpeaking ? (
                          <span className="text-emerald-400 font-bold">Konuşuyor...</span>
                        ) : (
                          'Dinliyor'
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MESAJ AKIŞI */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {(navMode === 'dm' ? dmMessages : messages).map(msg => {
            const senderName = msg.sender?.username || msg.guestName || 'Personel';
            const senderRole = msg.sender?.role?.name || msg.sender?.roleTitle || '';
            const senderRoleColor = msg.sender?.role?.color || '#9ca3af';
            const isMe = msg.senderUserId === currentUser?.id || msg.senderId === currentUser?.id;

            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
              >
                <div className="flex items-center gap-2 mb-1 px-1">
                  <span className="text-xs font-bold text-gray-200">{senderName}</span>
                  {senderRole && (
                    <span
                      className="text-[9px] px-2 py-0.5 rounded-md font-bold border"
                      style={{
                        color: senderRoleColor,
                        borderColor: `${senderRoleColor}40`,
                        backgroundColor: `${senderRoleColor}15`
                      }}
                    >
                      {senderRole}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-500">
                    {new Date(msg.createdAt).toLocaleTimeString('tr-TR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-3 text-xs ${
                    isMe
                      ? 'text-white rounded-br-sm shadow-lg'
                      : 'bg-[#0d1018] border border-white/[0.08] text-gray-100 rounded-bl-sm'
                  }`}
                  style={{ backgroundColor: isMe ? primaryColor : undefined }}
                >
                  <p className="leading-relaxed break-words">{msg.content}</p>

                  {/* OMNIO Fatura Kartı */}
                  {msg.omnioPayload && msg.omnioPayload.type === 'INVOICE_CARD' && (
                    <div className="mt-3 p-3.5 rounded-2xl bg-black/50 border border-white/15 space-y-2 min-w-[240px]">
                      <div className="flex items-center justify-between border-b border-white/10 pb-2">
                        <span className="text-[10px] font-black text-amber-300 uppercase flex items-center gap-1.5">
                          <Receipt size={13} /> OMNIO E-Belge
                        </span>
                        <span className="text-[10px] font-mono text-gray-300">
                          #{msg.omnioPayload.invoiceNo}
                        </span>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400">Cari / Müşteri</div>
                        <div className="text-xs font-bold text-white">
                          {msg.omnioPayload.customerName}
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-sm font-black text-emerald-400">
                          {msg.omnioPayload.amount}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold">
                          {msg.omnioPayload.status || 'Onay Bekliyor'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Dosya / Evrak Eki */}
                  {msg.fileUrl && (
                    <a
                      href={`${B2B_API_BASE}${msg.fileUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2.5 flex items-center gap-2.5 p-2.5 rounded-xl bg-black/40 hover:bg-black/60 border border-white/10 transition-all"
                    >
                      {msg.fileType === 'excel' ? (
                        <FileSpreadsheet size={18} className="text-emerald-400 shrink-0" />
                      ) : msg.fileType === 'image' ? (
                        <ImageIcon size={18} className="text-blue-400 shrink-0" />
                      ) : (
                        <FileText size={18} className="text-amber-400 shrink-0" />
                      )}
                      <span className="text-xs font-bold truncate flex-1">{msg.fileName}</span>
                      <ExternalLink size={13} className="text-gray-400" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* MESAJ VE EVRAK GÖNDERİM FORMU */}
        <form
          onSubmit={handleSendMessage}
          className="p-3.5 border-t border-white/[0.07] bg-[#080a10] flex items-center gap-2.5"
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.png,.jpg,.jpeg,.webp,.zip,.rar,.xml"
          />
          {navMode !== 'dm' && (
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="p-2.5 rounded-xl bg-white/[0.05] hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white transition-all"
              title="Excel, PDF veya Görsel Yükle"
            >
              <Paperclip size={16} />
            </button>
          )}

          <input
            type="text"
            placeholder={
              navMode === 'dm'
                ? `${activeDmUser?.username || 'Personel'} kişisine özel mesaj yazın...`
                : navMode === 'external'
                ? `Dış müşteri odasına (#${activeChannel?.name || 'genel'}) mesaj yazın...`
                : `#${activeChannel?.name || 'oda'} kanalına mesaj yazın...`
            }
            value={messageInput}
            onChange={e => setMessageInput(e.target.value)}
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white placeholder-gray-500 outline-none focus:border-white/25 transition-all"
          />

          <button
            type="submit"
            disabled={!messageInput.trim()}
            className="px-4 py-2.5 rounded-xl text-white font-bold text-xs flex items-center gap-1.5 disabled:opacity-40 transition-all shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Send size={14} /> Gönder
          </button>
        </form>
      </main>

      {/* 4. SAĞ SÜTUN: ŞİRKET KADROSU VE DEPARTMAN ROLLERİ */}
      {navMode === 'server' && showMemberSidebar && (
        <aside className="w-60 border-l border-white/[0.07] bg-[#090b11] p-3.5 overflow-y-auto hidden lg:block shrink-0 space-y-5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
              Şirket Kadrosu ({colleagues.length})
            </span>
            <span className="text-[10px] text-emerald-400 font-bold">
              {onlineUsers.length} Çevrimiçi
            </span>
          </div>

          {roles.map(role => {
            const roleMembers = colleagues.filter(u => u.roleId === role.id);
            if (roleMembers.length === 0) return null;

            return (
              <div key={role.id} className="space-y-1.5">
                <div
                  className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 px-1"
                  style={{ color: role.color }}
                >
                  {role.isOwnerRole && <Crown size={11} />}
                  <span>
                    {role.name} — {roleMembers.length}
                  </span>
                </div>

                {roleMembers.map(member => {
                  const isOnline = onlineUsers.some(u => u.userId === member.id);
                  return (
                    <div
                      key={member.id}
                      onClick={() => {
                        if (member.id !== currentUser?.id) {
                          setActiveDmUser(member);
                          setNavMode('dm');
                        }
                      }}
                      className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-white/[0.05] cursor-pointer transition-all"
                      title="Özel mesaj göndermek için tıklayın"
                    >
                      <div className="relative shrink-0">
                        <img
                          src={member.avatarUrl}
                          alt={member.username}
                          className="w-8 h-8 rounded-xl object-cover border border-white/10"
                        />
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#090b11] ${
                            isOnline ? 'bg-emerald-500' : 'bg-gray-600'
                          }`}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold truncate text-gray-200">
                          {member.username}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                          {member.customStatus || member.roleTitle}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </aside>
      )}

      {/* MODALLAR */}
      {/* Kişisel Ayarlar Modalı */}
      {showUserSettings && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0e1018] border border-white/15 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                <Settings size={16} style={{ color: primaryColor }} /> Kişisel Ayarlar
              </h3>
              <button
                onClick={() => setShowUserSettings(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 border-b border-white/10 bg-black/30 text-xs font-bold">
              <button
                type="button"
                onClick={() => setSettingsTab('profile')}
                className={`py-3 ${
                  settingsTab === 'profile' ? 'text-white border-b-2' : 'text-gray-400'
                }`}
                style={{ borderColor: settingsTab === 'profile' ? primaryColor : 'transparent' }}
              >
                👤 Profilim & Logo
              </button>
              <button
                type="button"
                onClick={() => setSettingsTab('audio')}
                className={`py-3 ${
                  settingsTab === 'audio' ? 'text-white border-b-2' : 'text-gray-400'
                }`}
                style={{ borderColor: settingsTab === 'audio' ? primaryColor : 'transparent' }}
              >
                🎙️ Ses & Donanım
              </button>
            </div>

            <form onSubmit={handleSaveUserSettings} className="p-5 space-y-4">
              {settingsTab === 'profile' ? (
                <>
                  <div className="flex items-center gap-4">
                    <img
                      src={editAvatar || currentUser?.avatarUrl}
                      alt="Avatar"
                      className="w-16 h-16 rounded-2xl object-cover border border-white/15 shrink-0"
                    />
                    <div className="space-y-1.5 flex-1">
                      <input
                        ref={avatarFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarFileSelect}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => avatarFileInputRef.current?.click()}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-xs font-bold"
                      >
                        <Upload size={13} /> Fotoğraf / Logo Seç
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 mb-1">
                      Görünen Adınız
                    </label>
                    <input
                      type="text"
                      required
                      value={editUsername}
                      onChange={e => setEditUsername(e.target.value)}
                      className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 mb-1">Durum</label>
                      <select
                        value={editStatus}
                        onChange={e => setEditStatus(e.target.value)}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                      >
                        <option value="online">🟢 Çevrimiçi</option>
                        <option value="busy">🔴 Toplantıda / Meşgul</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 mb-1">
                        Durum Notu
                      </label>
                      <input
                        type="text"
                        value={editCustomStatus}
                        onChange={e => setEditCustomStatus(e.target.value)}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 mb-1">
                      Mikrofon Girişi
                    </label>
                    <select
                      value={selectedInput}
                      onChange={e => setSelectedInput(e.target.value)}
                      className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                    >
                      <option value="default">Varsayılan Mikrofon</option>
                      {audioInputs.map(d => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || 'Mikrofon'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-400 mb-1">
                      Hoparlör / Kulaklık Çıkışı
                    </label>
                    <select
                      value={selectedOutput}
                      onChange={e => setSelectedOutput(e.target.value)}
                      className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
                    >
                      <option value="default">Varsayılan Hoparlör</option>
                      {audioOutputs.map(d => (
                        <option key={d.deviceId} value={d.deviceId}>
                          {d.label || 'Hoparlör'}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowUserSettings(false)}
                  className="px-4 py-2 text-xs text-gray-400"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={savingSettings}
                  className="px-5 py-2 rounded-xl text-xs font-black text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rol Yönetimi Modalı */}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0e1018] border border-white/15 rounded-3xl p-5 w-full max-w-md space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-black uppercase flex items-center gap-2">
                <Crown size={16} className="text-amber-400" /> Şirket Rolleri & Yetkiler
              </h3>
              <button
                onClick={() => setShowRoleModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateRole} className="flex gap-2">
              <input
                type="text"
                placeholder="Yeni Rol Adı (Örn: Ön Muhasebe)"
                value={newRoleName}
                onChange={e => setNewRoleName(e.target.value)}
                className="flex-1 bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
              />
              <input
                type="color"
                value={newRoleColor}
                onChange={e => setNewRoleColor(e.target.value)}
                className="w-9 h-9 rounded-xl bg-black/50 border border-white/10 cursor-pointer"
              />
              <button
                type="submit"
                className="px-3 py-2 rounded-xl text-xs font-bold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Ekle
              </button>
            </form>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {colleagues.map(col => (
                <div
                  key={col.id}
                  className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={col.avatarUrl}
                      alt={col.username}
                      className="w-7 h-7 rounded-lg object-cover"
                    />
                    <span className="text-xs font-bold truncate">{col.username}</span>
                  </div>
                  <select
                    value={col.roleId || ''}
                    onChange={e => handleAssignRole(col.id, e.target.value)}
                    className="bg-black/60 border border-white/15 rounded-lg px-2 py-1 text-xs text-white outline-none"
                  >
                    {roles.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Denetim & Hareket Kayıtları Modalı */}
      {showAuditModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0e1018] border border-white/15 rounded-3xl p-6 w-full max-w-2xl space-y-4 shadow-2xl flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div>
                <h3 className="text-sm font-black uppercase flex items-center gap-2 text-emerald-400">
                  <Shield size={16} /> {company?.name} — Denetim & Hareket Kayıtları
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  Paylaşılan evraklar, oluşturulan dış müşteri linkleri ve güvenlik olayları.
                </p>
              </div>
              <button
                onClick={() => setShowAuditModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {auditLogs.length === 0 ? (
                <div className="py-12 text-center text-xs text-gray-500">
                  Henüz kaydedilmiş bir şirket hareketi bulunmuyor.
                </div>
              ) : (
                auditLogs.map(log => (
                  <div
                    key={log.id}
                    className={`p-3.5 rounded-2xl border flex items-start justify-between gap-4 ${
                      log.severity === 'CRITICAL'
                        ? 'bg-red-500/10 border-red-500/30'
                        : log.severity === 'WARN'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-white/[0.03] border-white/10'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-white">{log.title}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-gray-300 font-bold">
                          {log.actorName} {log.actorRole ? `(${log.actorRole})` : ''}
                        </span>
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed">{log.description}</p>
                    </div>

                    <div className="text-right shrink-0 text-[10px] text-gray-400 font-mono">
                      <div>{new Date(log.createdAt).toLocaleDateString('tr-TR')}</div>
                      <div>{new Date(log.createdAt).toLocaleTimeString('tr-TR')}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Yeni Oda Açma Modalı */}
      {showCreateChannelModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={handleCreateChannel}
            className="bg-[#0e1018] border border-white/15 rounded-2xl p-5 w-full max-w-xs space-y-4 shadow-2xl"
          >
            <h3 className="text-sm font-black">
              {newChannelScope === 'external'
                ? '🌐 Yeni Dış Müşteri Odası Aç'
                : newChannelType === 'voice'
                ? '🔊 Yeni Sesli Oda Aç'
                : '# Yeni Metin Kanalı Aç'}
            </h3>
            <input
              type="text"
              required
              placeholder="Oda adı (Örn: muhasebe-evrak)"
              value={newChannelName}
              onChange={e => setNewChannelName(e.target.value)}
              className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-xs text-white outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreateChannelModal(false)}
                className="px-3 py-1.5 text-xs text-gray-400"
              >
                İptal
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-xl text-xs font-bold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Oluştur
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default OmnioTemplate;